<template>
  <section v-if="shouldRender" id="integrations" class="relative overflow-hidden bg-slate-25 section-padding">
    <div class="absolute inset-x-0 top-0 -z-10 h-60 bg-gradient-to-b from-hilab/10 via-transparent to-transparent"></div>
    <div class="container-full-width">
      <div class="text-center">
        <span v-if="badge" class="badge">{{ badge }}</span>
        <h2 v-if="title" class="section-title mt-6">{{ title }}</h2>
        <p v-if="description" class="section-subtitle mx-auto">
          {{ description }}
        </p>
      </div>
      <div class="mt-14 grid gap-6 md:grid-cols-2">
        <div v-if="highlightItems.length" class="rounded-3xl border border-slate-100 bg-white p-6 shadow-lg">
          <h3 class="text-lg font-semibold text-slate-900">Các kết nối chiến lược</h3>
          <ul class="mt-6 space-y-4 text-sm text-slate-500">
            <li
              v-for="item in highlightItems"
              :key="item.title || item.description"
              class="group flex items-start gap-3 rounded-2xl border border-transparent p-4 transition hover:border-hilab/20 hover:bg-hilab/10"
            >
            <div class="icon-circle mt-1 h-8 w-8 bg-hilab/10 text-hilab">
              <LucideIcon 
                :name="getIntegrationIcon(item.icon)" 
                :size="16" 
                class="text-hilab" 
              />
            </div>
              <div>
                <p class="text-sm font-semibold text-slate-900">{{ item.title }}</p>
                <p class="mt-1 text-xs text-slate-500">{{ item.description }}</p>
              </div>
            </li>
          </ul>
        </div>
        <div v-if="categories.length" class="rounded-3xl border border-slate-100 bg-white/80 p-6 shadow-lg">
          <h3 class="text-lg font-semibold text-slate-900">Danh mục tích hợp</h3>
          <div class="mt-6 space-y-4 text-sm text-slate-500">
            <div v-for="category in categories" :key="category.title" class="rounded-2xl border border-slate-100 bg-slate-25/60 p-4">
              <p class="text-sm font-semibold text-slate-900">{{ category.title }}</p>
              <p v-if="category.description" class="mt-1 text-xs text-slate-500">{{ category.description }}</p>
              <ul v-if="category.items?.length" class="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
                <li v-for="item in category.items" :key="item" class="rounded-full bg-white px-3 py-1 shadow-sm">
                  {{ item }}
                </li>
              </ul>
            </div>
          </div>
          <p v-if="footerNote" class="mt-6 rounded-2xl bg-slate-25 p-4 text-xs leading-relaxed text-slate-500">
            {{ footerNote }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import LucideIcon from '../../dashboard/components/LucideIcon.vue';

const toArray = value => (Array.isArray(value) ? value : value && typeof value === 'object' ? Object.values(value) : []);

const props = defineProps({
  content: {
    type: Object,
    default: () => ({}),
  },
});

const sectionContent = computed(() => props.content || {});
const highlightItems = computed(() =>
  toArray(sectionContent.value.highlights).filter(item => item?.title || item?.description)
);
const categories = computed(() => {
  const categorized = toArray(sectionContent.value.categories).filter(category => category?.title || (Array.isArray(category?.items) && category.items.length));
  if (categorized.length) {
    return categorized;
  }

  const legacyIntegrations = toArray(sectionContent.value.integrations);
  if (legacyIntegrations.length) {
    return legacyIntegrations
      .map(integration => ({
        title: integration.title,
        description: integration.description,
        items: toArray(integration.items).length ? toArray(integration.items) : toArray(integration.brands),
      }))
      .filter(category => category.title || category.items.length);
  }

  return [];
});
const badge = computed(() => sectionContent.value.badge || '');
const title = computed(() => {
  if (sectionContent.value.title) {
    return sectionContent.value.title;
  }
  if (highlightItems.value.length || categories.value.length) {
    return 'Kết nối với hệ sinh thái bạn đang sử dụng';
  }
  return '';
});
const description = computed(() => sectionContent.value.description || '');
const footerNote = computed(() => sectionContent.value.footer_note || '');
const shouldRender = computed(() => title.value || description.value || highlightItems.value.length || categories.value.length);

const getIntegrationIcon = (iconType) => {
  const iconMap = {
    'data': 'database',
    'bot': 'bot',
    'ai': 'brain-cog',
    'automation': 'refresh-cw',
    'integration': 'zap',
    'analytics': 'chart-spline',
    'default': 'check-check'
  };
  return iconMap[iconType] || iconMap['default'];
};
</script>
